import { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { API } from "@/assets";
import { Quote, ChevronLeft, ChevronRight, Star, PenLine, Loader2 } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const mediaUrl = (url) => (url && url.startsWith("http") ? url : `${BACKEND}${url}`);

const inputCls =
  "w-full bg-obsidian/70 border border-white/12 rounded-xl px-4 py-3 text-sm text-ice placeholder:text-ice/35 focus:outline-none focus:border-venom/70 focus:ring-2 focus:ring-venom/20 transition-all duration-300";

const FALLBACK = [
  {
    quote: "Heather rebuilt my mix from the ground up — the 808s finally hit the way I heard them in my head. Three-day turnaround, zero excuses. DGE is my permanent home for mixing.",
    name: "Marcus 'Young Cee' Bell",
    role: "Independent Artist · Mixing & Mastering",
  },
  {
    quote: "I leased two beats and came back for the exclusive. The licensing paperwork was the cleanest I've ever seen — everything spelled out, ASCAP splits handled right.",
    name: "Deja Monroe",
    role: "R&B Artist · Beat Licensing",
  },
  {
    quote: "HotRod sat with me for one consultation and restructured my whole rollout. First release under his plan did 10x my previous numbers. Real business coaching, not hype.",
    name: "Trey 'T-Way' Simmons",
    role: "Hip-Hop Artist · Artist Development",
  },
  {
    quote: "From production to distribution, everything lived under one roof. My EP came out sounding like a major label record — because the standard here is major.",
    name: "Alina Reign",
    role: "Singer-Songwriter · Full Production",
  },
];

export const TestimonialsSection = () => {
  const [items, setItems] = useState(FALLBACK);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [form, setForm] = useState({ name: "", role: "", quote: "" });
  const [sending, setSending] = useState(false);
  const [reviewMsg, setReviewMsg] = useState("");

  useEffect(() => {
    fetch(`${API}/testimonials`)
      .then((r) => r.json())
      .then((d) => {
        if (d.testimonials && d.testimonials.length > 0) setItems(d.testimonials);
      })
      .catch(() => {});
  }, []);

  const next = useCallback(() => setIndex((i) => (i + 1) % items.length), [items.length]);
  const prev = () => setIndex((i) => (i - 1 + items.length) % items.length);

  useEffect(() => {
    if (paused) return;
    const t = setInterval(next, 5000);
    return () => clearInterval(t);
  }, [paused, next]);

  const current = items[index % items.length];

  const submitReview = async (e) => {
    e.preventDefault();
    setReviewMsg("");
    setSending(true);
    try {
      const res = await fetch(`${API}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Submit failed");
      setReviewMsg(data.message || "Thanks! Your review was submitted for approval.");
      setForm({ name: "", role: "", quote: "" });
    } catch (err) {
      setReviewMsg(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="relative py-20 lg:py-28 overflow-hidden"
      data-testid="testimonials-section"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-8">
        <Reveal className="text-center">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-venom font-medium mb-4">
            Artist Success Stories
          </p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white leading-tight">
            Word from the booth.
          </h2>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative mt-12" data-testid="testimonial-carousel">
            <Quote size={44} className="absolute -top-5 left-1/2 -translate-x-1/2 text-crystal/40" aria-hidden="true" />
            <div className="bg-charcoal border border-crystal/25 rounded-2xl px-6 sm:px-12 pt-14 pb-10 min-h-[240px] flex flex-col justify-center">
              <AnimatePresence mode="wait">
                <motion.figure
                  key={index}
                  initial={{ opacity: 0, x: 40 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -40 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center"
                >
                  <div className="flex justify-center gap-1 mb-5" aria-hidden="true">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} className="text-venom" fill="currentColor" />
                    ))}
                  </div>
                  {current.photo && (
                    <img
                      src={mediaUrl(current.photo)}
                      alt={`Fan mail from ${current.name}`}
                      className="mx-auto mb-6 w-32 h-32 sm:w-36 sm:h-36 object-cover rounded-xl border-2 border-white/25 -rotate-2 shadow-[0_10px_30px_rgba(0,0,0,0.45)]"
                      data-testid="testimonial-photo"
                    />
                  )}
                  <blockquote className="text-base sm:text-lg text-ice/85 leading-relaxed max-w-2xl mx-auto" data-testid="testimonial-quote">
                    &ldquo;{current.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-6">
                    <p className="font-display text-white text-sm font-bold" data-testid="testimonial-name">{current.name}</p>
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-crystal mt-1">{current.role}</p>
                  </figcaption>
                </motion.figure>
              </AnimatePresence>
            </div>

            <div className="mt-6 flex items-center justify-center gap-4">
              <button
                onClick={prev}
                aria-label="Previous testimonial"
                className="w-10 h-10 rounded-full border border-crystal/40 flex items-center justify-center text-ice/70 hover:border-venom hover:text-venom transition-all duration-300"
                data-testid="testimonial-prev"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="flex gap-2" data-testid="testimonial-dots">
                {items.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setIndex(i)}
                    aria-label={`Go to testimonial ${i + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-400 ${i === index ? "w-8 bg-venom" : "w-1.5 bg-white/20 hover:bg-white/40"}`}
                    data-testid={`testimonial-dot-${i}`}
                  />
                ))}
              </div>
              <button
                onClick={next}
                aria-label="Next testimonial"
                className="w-10 h-10 rounded-full border border-crystal/40 flex items-center justify-center text-ice/70 hover:border-venom hover:text-venom transition-all duration-300"
                data-testid="testimonial-next"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.25}>
          <div className="mt-10 text-center">
            <button
              onClick={() => setReviewOpen(!reviewOpen)}
              className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] border border-crystal/40 text-crystal rounded-full px-6 py-3 hover:border-venom hover:text-venom transition-all duration-300"
              data-testid="leave-review-button"
            >
              <PenLine size={14} /> Leave a Review
            </button>
            <AnimatePresence>
              {reviewOpen && (
                <motion.form
                  onSubmit={submitReview}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.35 }}
                  className="overflow-hidden"
                  data-testid="review-form"
                >
                  <div className="mt-6 bg-charcoal border border-crystal/25 rounded-2xl p-6 text-left max-w-xl mx-auto">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name *" maxLength={120} className={inputCls} data-testid="review-input-name" />
                      <input value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} placeholder="Who you are (e.g. R&B Artist)" maxLength={160} className={inputCls} data-testid="review-input-role" />
                    </div>
                    <textarea required rows={3} value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} placeholder="Your experience with the label *" maxLength={1000} className={`${inputCls} resize-none mt-4`} data-testid="review-input-quote" />
                    {reviewMsg && <p className="mt-3 text-sm text-venom" data-testid="review-message">{reviewMsg}</p>}
                    <button type="submit" disabled={sending} className="mt-4 inline-flex items-center gap-2 bg-venom text-obsidian font-bold text-sm px-6 py-3 rounded-full hover:glow-venom active:scale-95 disabled:opacity-60 transition-all duration-300" data-testid="review-submit">
                      {sending ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />}
                      {sending ? "Sending…" : "Submit Review"}
                    </button>
                    <p className="mt-3 text-[11px] text-ice/40">Reviews appear on the site after the label approves them.</p>
                  </div>
                </motion.form>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
